export interface FocusArea {
  name: string;
  title: string;
  description: string;
  symptoms: string[];
  impacts: string[];
  solutions: string[];
  nextSteps: {
    step: string;
    action: string;
  }[];
  insight: string;
}

export const focusAreas: Record<string, FocusArea> = {
  automation: {
    name: "Automation",
    title: "Smart Automation",
    description:
      "Your biggest opportunity is automating routine customer interactions. You're spending too much time answering the same questions repeatedly, and customers are waiting too long for simple answers. AI chatbots can handle these instantly, 24/7.",
    symptoms: [
      "Team overwhelmed with repetitive questions",
      "Long wait times for simple inquiries",
      "No support available outside business hours",
      "Staff frustrated answering same questions daily",
      "Customers can't get quick answers to common issues",
    ],
    impacts: [
      "Lost sales when customers can't get immediate help",
      "Team burnout from repetitive work",
      "Poor customer experience with slow responses",
      "Higher support costs than necessary",
      "Staff time wasted on routine tasks instead of complex issues",
    ],
    solutions: [
      "AI Chatbot Implementation: Deploy an intelligent chatbot to handle common questions instantly, 24/7",
      "Knowledge Base Automation: Create searchable resources that customers and AI can access",
      "Smart Routing: Automatically direct complex issues to the right team members",
      "Response Templates: Build AI-powered responses that feel personal but save time",
      "Gradual Rollout: Start with your top 10-20 most common questions and expand from there",
    ],
    nextSteps: [
      {
        step: "Identify Top Questions",
        action:
          "In the next week, list the 20 most frequently asked questions your team handles daily",
      },
      {
        step: "Calculate Time Savings",
        action:
          "Estimate how many hours per week your team spends on these repetitive questions",
      },
      {
        step: "Research AI Solutions",
        action:
          "Explore 2-3 chatbot platforms that integrate with your current systems",
      },
      {
        step: "Start Small",
        action:
          "Launch with one category of questions (like hours, pricing, or basic product info) and measure results",
      },
    ],
    insight:
      "The best AI customer service solutions don't replace your team - they free them up to focus on the interactions that truly need a human touch. Start by automating the simple stuff, so your people can shine on the complex stuff.",
  },
  personalization: {
    name: "Personalization",
    title: "Personalized Experience",
    description:
      "Your customers want to feel recognized and valued, not like just another ticket number. AI can help you deliver personalized experiences at scale by remembering preferences, past interactions, and anticipating needs - without adding more work for your team.",
    symptoms: [
      "Generic, one-size-fits-all responses",
      "Customers have to repeat information",
      "No memory of past interactions",
      "Can't tailor recommendations to individual needs",
      "Customers feel like strangers every time they contact you",
    ],
    impacts: [
      "Lower customer loyalty and repeat business",
      "Frustrated customers who feel undervalued",
      "Missed opportunities to upsell or cross-sell",
      "Competitors winning with better personalized service",
      "Customer churn to businesses that 'know them better'",
    ],
    solutions: [
      "Customer Profile AI: Implement systems that remember preferences, history, and context",
      "Predictive Recommendations: Use AI to suggest relevant products or solutions based on patterns",
      "Dynamic Responses: Customize chatbot conversations based on customer type and history",
      "Sentiment Analysis: Detect customer mood and adjust tone appropriately",
      "Proactive Engagement: Reach out with personalized offers or solutions before customers ask",
    ],
    nextSteps: [
      {
        step: "Map Customer Journey",
        action:
          "In 2 weeks, document all the touchpoints where personalization would add value",
      },
      {
        step: "Collect Key Data Points",
        action:
          "Identify what customer information you have and what you're missing for personalization",
      },
      {
        step: "Start with Basics",
        action:
          "Begin with simple personalization like using names and remembering past purchases",
      },
      {
        step: "Implement AI Learning",
        action:
          "Deploy AI that learns from each interaction to improve future personalization",
      },
    ],
    insight:
      "Personalization isn't about knowing everything about your customers - it's about remembering what matters to them and making each interaction feel relevant. AI makes this possible without drowning your team in data.",
  },
  integration: {
    name: "Integration",
    title: "Seamless Integration",
    description:
      "Your customer data is scattered across different systems, making it hard to deliver consistent, informed service. AI works best when it can access all your customer information in one place, creating a complete picture that helps both your team and automated systems serve customers better.",
    symptoms: [
      "Customer information spread across multiple platforms",
      "Team switches between systems to find answers",
      "Different channels don't share information",
      "Duplicate or conflicting customer data",
      "Can't see complete customer history quickly",
    ],
    impacts: [
      "Slow response times while searching for information",
      "Inconsistent answers across different channels",
      "Frustrated customers repeating themselves",
      "Team inefficiency and higher costs",
      "Missed insights hiding in disconnected data",
    ],
    solutions: [
      "Unified Customer Platform: Integrate all customer data into one accessible system",
      "API Connections: Link your chatbot to CRM, order systems, and support tools",
      "Omnichannel Support: Ensure AI and team can see interactions from all channels",
      "Data Synchronization: Keep customer information updated across all platforms automatically",
      "Single Source of Truth: Create one master customer record that feeds everything",
    ],
    nextSteps: [
      {
        step: "System Audit",
        action:
          "In 30 days, list every system where you store or access customer information",
      },
      {
        step: "Identify Critical Gaps",
        action:
          "Find the biggest pain points where disconnected data causes problems",
      },
      {
        step: "Prioritize Integrations",
        action:
          "Choose 2-3 most important connections to make first for maximum impact",
      },
      {
        step: "Implement Step by Step",
        action:
          "Connect systems gradually, testing each integration before moving to the next",
      },
    ],
    insight:
      "Integration isn't just a technical project - it's about giving everyone (including your AI) the full story. When your systems talk to each other, your customer service becomes faster, smarter, and more consistent across every touchpoint.",
  },
};
